import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import SEO from "@/components/SEO";
import SaveToPmosButton from "@/components/womens_health/SaveToPmosButton";
import {
  Zap, Heart, Target, ChevronRight, ChevronLeft,
  CheckCircle2, Loader2, Activity, Shield, ArrowRight, Globe,
  AlertTriangle, TrendingDown,
} from "lucide-react";
import { toast } from "sonner";
import { trackTrialStarted } from "@/utils/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICON_MAP = { zap: Zap, heart: Heart, target: Target, activity: Activity, shield: Shield };
// Total logical steps in the funnel (goal, symptoms, analyzing, result, impact, email-or-paywall)
const STEP_COUNT = 6;
const LS_KEY = "nutramilo_assessment_draft";

const LANG_OPTIONS = [
  { code: "bg", label: "Български", flag: "BG" },
  { code: "en", label: "English", flag: "EN" },
  { code: "es", label: "Espanol", flag: "ES" },
];

function getLangField(obj, field, lang) {
  return obj?.[`${field}_${lang}`] || obj?.[`${field}_en`] || "";
}

export default function SymptomAssessment() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const bg = language === "bg";

  const [step, setStep] = useState(0);
  const [goals, setGoals] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [animProgress, setAnimProgress] = useState(0);
  const [reportLang, setReportLang] = useState(language);
  const [microUnlockLoading, setMicroUnlockLoading] = useState(false);
  const [planCheckoutLoading, setPlanCheckoutLoading] = useState(null); // 'core' | 'pro' | null
  const [paywallPlans, setPaywallPlans] = useState(null); // { core, pro } once fetched
  const [annualBilling, setAnnualBilling] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [emailCaptureLoading, setEmailCaptureLoading] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(false);

  // Try to resume from localStorage (anonymous lead returning after tab close).
  // We previously relied on an httpOnly cookie set by /leads/capture-email but
  // that path forced withCredentials=true and broke when the upstream proxy
  // injects Access-Control-Allow-Origin: * (browsers reject credentialed
  // responses with wildcard origin → axios threw "error" toast even though the
  // email was sent server-side).
  useEffect(() => {
    if (user) return; // auth'd users never see resume; they go through /assessment/latest
    let saved = null;
    try {
      const raw = localStorage.getItem("nutramilo_anon_resume");
      if (raw) saved = JSON.parse(raw);
    } catch { saved = null; }
    if (saved?.assessment_id) {
      axios.get(`${API}/assessment/${saved.assessment_id}`)
        .then(({ data: assessDoc }) => {
          setResult({ ...assessDoc, assessment_id: saved.assessment_id });
          setSelectedGoal(assessDoc.goal);
          setSelectedSymptoms(assessDoc.symptoms || []);
          if (saved.email) { setLeadEmail(saved.email); setEmailCaptured(true); }
          setStep(3);  // land back on the result + impact CTA
        })
        .catch(() => {});
    }
  }, [API, user]);

  // Restore draft from localStorage on first mount (pre-register safety net)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.selectedGoal) setSelectedGoal(draft.selectedGoal);
      if (Array.isArray(draft.selectedSymptoms)) setSelectedSymptoms(draft.selectedSymptoms);
    } catch { /* ignore corrupted draft */ }
  }, []);

  // iter396 — fetch geo-aware pricing for paywall (BG / EU / T1 / T3 PPP)
  useEffect(() => {
    const market = language === "bg" ? "bg" : language === "en" ? "eu" : "eu";
    axios.get(`${API}/pricing/catalog?market=${market}`)
      .then(({ data }) => {
        const plans = (data?.plans || []).reduce((acc, p) => {
          acc[p.id] = p;
          return acc;
        }, {});
        setPaywallPlans({ core: plans.core, pro: plans.pro, market: data.market || market });
      })
      .catch(() => setPaywallPlans(null)); // fallback to hardcoded prices
  }, [language]);

  // Auto-save draft on every change
  useEffect(() => {
    if (selectedGoal || selectedSymptoms.length) {
      try {
        localStorage.setItem(LS_KEY, JSON.stringify({ selectedGoal, selectedSymptoms }));
      } catch { /* quota — ignore */ }
    }
  }, [selectedGoal, selectedSymptoms]);

  // Fetch config + redirect if user already has an assessment (unless retaking)
  const [searchParams] = useSearchParams();
  const isRetake = searchParams.get("retake") === "true" || searchParams.get("retake") === "1";

  useEffect(() => {
    axios.get(`${API}/assessment/config`).then(r => {
      setGoals(r.data.goals || []);
      setSymptoms(r.data.symptoms || []);
    }).catch(() => {});

    // Block duplicate assessments for authenticated users — UNLESS they
    // explicitly clicked "Повтори оценка" (retake=true). Phenotype/symptom
    // answers may need correcting later, so retake is always allowed.
    if (user && !isRetake) {
      axios.get(`${API}/assessment/latest`).then(r => {
        if (r.data?.has_assessment) {
          toast.info(bg
            ? "Вече сте направили оценка. Пренасочваме към вашия профил."
            : "You already have an assessment. Redirecting to your profile.");
          setTimeout(() => navigate("/dashboard"), 1500);
        }
      }).catch(() => {});
    }
  }, [user, bg, navigate, API, isRetake]);

  const toggleSymptom = (id) => {
    setSelectedSymptoms(prev =>
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const runAnalysis = async () => {
    setStep(2);
    setAnalyzing(true);
    setAnimProgress(0);

    // Smooth progress animation
    const interval = setInterval(() => {
      setAnimProgress(p => {
        if (p >= 90) { clearInterval(interval); return 90; }
        return p + Math.random() * 15;
      });
    }, 300);

    try {
      const { data } = await axios.post(`${API}/assessment/analyze`, {
        goal: selectedGoal,
        symptoms: selectedSymptoms,
        severity: 5,
      });
      clearInterval(interval);
      setAnimProgress(100);
      setTimeout(() => {
        setResult(data);
        setStep(3);
        setAnalyzing(false);
      }, 600);
    } catch {
      clearInterval(interval);
      setAnalyzing(false);
      setStep(1);
    }
  };

  const progress = ((step + 1) / STEP_COUNT) * 100;

  return (
    <div className={`min-h-screen ${isDark ? "bg-background" : "bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4]"}`} data-testid="symptom-assessment">
      <SEO title={bg ? "Здравна оценка" : "Health Assessment"} noindex />

      {/* Header */}
      <header className={`sticky top-0 z-40 backdrop-blur-xl border-b safe-top ${isDark ? "bg-[hsl(223,47%,8%)]/90 border-[hsl(223,47%,15%)]" : "bg-white/90 border-gray-100"}`}>
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" />
            <img src="/nutramilo-logo.png" alt="Nutramilo" className="hidden sm:block h-8 w-auto object-contain" onError={e => e.target.style.display = "none"} />
          </div>
          {step < 3 && (
            <span className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
              {bg ? `Стъпка ${step + 1} от 3` : `Step ${step + 1} of 3`}
            </span>
          )}
        </div>
        {step < 3 && <Progress value={progress} className="h-1" />}
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 sm:py-10">
        {/* Step 0: Goal Selection */}
        {step === 0 && (
          <div className="animate-fade-in-up" data-testid="step-goal">
            <div className="text-center mb-8">
              <h1 className={`text-2xl sm:text-3xl font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Каква е основната ви цел?" : "What is your primary goal?"}
              </h1>
              <p className={`text-sm sm:text-base ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Изберете какво искате да подобрите" : "Choose what you want to improve"}
              </p>
            </div>
            <div className="space-y-3">
              {goals.map(g => {
                const Icon = ICON_MAP[g.icon] || Zap;
                const selected = selectedGoal === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => setSelectedGoal(g.id)}
                    data-testid={`goal-${g.id}`}
                    className={`w-full flex items-center gap-4 p-4 sm:p-5 rounded-2xl border-2 transition-all text-left ${
                      selected
                        ? `border-[${g.color}] shadow-lg scale-[1.01]`
                        : isDark
                        ? "border-[hsl(223,47%,18%)] hover:border-[hsl(223,47%,25%)]"
                        : "border-gray-100 hover:border-gray-200 hover:shadow-md"
                    } ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-white"}`}
                    style={selected ? { borderColor: g.color, boxShadow: `0 4px 20px ${g.color}20` } : {}}
                  >
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${g.color}15` }}>
                      <Icon className="w-6 h-6" style={{ color: g.color }} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-semibold text-sm sm:text-base ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                        {bg ? g.label_bg : g.label_en}
                      </p>
                    </div>
                    {selected && <CheckCircle2 className="w-5 h-5 flex-shrink-0" style={{ color: g.color }} />}
                  </button>
                );
              })}
            </div>
            <div className="mt-8 flex justify-end">
              <Button
                onClick={() => setStep(1)}
                disabled={!selectedGoal}
                className="gradient-primary text-white px-6 h-12 rounded-xl"
                data-testid="goal-next-btn"
              >
                {bg ? "Напред" : "Continue"} <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 1: Symptom Selection */}
        {step === 1 && (
          <div className="animate-fade-in-up" data-testid="step-symptoms">
            <div className="text-center mb-6">
              <h1 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Кои от тези симптоми изпитвате?" : "Which of these symptoms do you experience?"}
              </h1>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Изберете всички приложими (минимум 2)" : "Select all that apply (minimum 2)"}
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {symptoms.map(s => {
                const checked = selectedSymptoms.includes(s.id);
                return (
                  <button
                    key={s.id}
                    onClick={() => toggleSymptom(s.id)}
                    data-testid={`symptom-${s.id}`}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left text-sm ${
                      checked
                        ? isDark
                          ? "bg-[#0EA5E9]/10 border-[#0EA5E9]/50 text-[#0EA5E9]"
                          : "bg-[#0EA5E9]/5 border-[#0EA5E9]/40 text-[#0EA5E9]"
                        : isDark
                        ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)] text-gray-300 hover:border-[hsl(223,47%,25%)]"
                        : "bg-white border-gray-100 text-gray-600 hover:border-gray-200"
                    }`}
                  >
                    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 ${
                      checked ? "bg-[#0EA5E9] border-[#0EA5E9]" : isDark ? "border-gray-600" : "border-gray-300"
                    }`}>
                      {checked && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
                    </div>
                    <span className="font-medium">{bg ? s.label_bg : s.label_en}</span>
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex items-center justify-between">
              <Button variant="ghost" onClick={() => setStep(0)} className="h-11">
                <ChevronLeft className="w-4 h-4 mr-1" /> {bg ? "Назад" : "Back"}
              </Button>
              <Button
                onClick={runAnalysis}
                disabled={selectedSymptoms.length < 2}
                className="gradient-primary text-white px-6 h-12 rounded-xl"
                data-testid="analyze-btn"
              >
                {bg ? "Анализирай" : "Analyze"} <Activity className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Analyzing Animation */}
        {step === 2 && analyzing && (
          <div className="animate-fade-in-up flex flex-col items-center justify-center py-16 text-center" data-testid="step-analyzing">
            <div className="relative w-24 h-24 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-[#0EA5E9]/20" />
              <div className="absolute inset-0 rounded-full border-4 border-t-[#0EA5E9] animate-spin" />
              <div className="absolute inset-3 rounded-full bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] flex items-center justify-center">
                <Activity className="w-8 h-8 text-white" />
              </div>
            </div>
            <h2 className={`text-xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
              {bg ? "Анализираме модела на симптомите ви..." : "Analyzing your symptom pattern..."}
            </h2>
            <p className={`text-sm mb-6 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              {bg ? "Сравняваме симптомите ви с 5 метаболитни модела" : "Matching your symptoms against 5 metabolic patterns"}
            </p>
            <div className="w-full max-w-xs">
              <Progress value={animProgress} className="h-2" />
              <p className={`text-xs mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>{Math.round(animProgress)}%</p>
            </div>
          </div>
        )}

        {/* Step 3: Result + CTA to Impact */}
        {step === 3 && result && (
          <div className="animate-fade-in-up" data-testid="step-result">
            {/* Report Language Selector */}
            <div className={`flex items-center justify-center gap-2 mb-5`} data-testid="report-lang-selector">
              <Globe className={`w-4 h-4 ${isDark ? "text-gray-500" : "text-gray-400"}`} />
              {LANG_OPTIONS.map(opt => (
                <button
                  key={opt.code}
                  onClick={() => setReportLang(opt.code)}
                  data-testid={`report-lang-${opt.code}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    reportLang === opt.code
                      ? "bg-[#0EA5E9] text-white shadow-md"
                      : isDark ? "bg-[hsl(223,47%,14%)] text-gray-400 hover:text-white" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                >
                  {opt.flag}
                </button>
              ))}
            </div>

            {/* Big headline */}
            <div className="text-center mb-5">
              <p className={`text-[11px] uppercase tracking-wider font-semibold mb-1 ${isDark ? "text-emerald-400" : "text-emerald-600"}`}>
                <CheckCircle2 className="w-3.5 h-3.5 inline-block mr-1" />
                {reportLang === "bg" ? "Анализът е готов" : reportLang === "es" ? "Analisis listo" : "Analysis ready"}
              </p>
              <h2 className={`text-xl sm:text-2xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {/* iter312 — neutral framing (was: "Анализирахме вашия метаболитен профил").
                    "Метаболитен профил" звучи като клинично лабораторно изследване — заменено
                    с образователно описание per MDR Rule 11. */}
                {reportLang === "bg" ? "Анализирахме модела на симптомите ви" : reportLang === "es" ? "Analizamos su patron de sintomas" : "We analyzed your symptom pattern"}
              </h2>
            </div>

            {/* Insight Card */}
            <Card className={`overflow-hidden mb-4 ${isDark ? "bg-card border-border" : "border-0 shadow-xl"}`}>
              <div className="h-2" style={{ backgroundColor: result.dominant_pattern.color }} />
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${result.dominant_pattern.color}15` }}>
                    {(() => { const I = ICON_MAP[result.dominant_pattern.icon] || Activity; return <I className="w-6 h-6" style={{ color: result.dominant_pattern.color }} />; })()}
                  </div>
                  <div>
                    <p className={`text-xs font-medium uppercase tracking-wider flex items-center gap-1 ${isDark ? "text-amber-400" : "text-amber-600"}`}>
                      <AlertTriangle className="w-3 h-3" />
                      {/* iter312 — patient-safe label (was: "Открит метаболитен дисбаланс").
                          Replaced with neutral pattern-recognition framing per MDR Rule 11. */}
                      {reportLang === "bg" ? "Образователен модел на симптоми" : reportLang === "es" ? "Patron educativo de sintomas" : "Educational symptom pattern"}
                    </p>
                    <h2 className={`text-lg sm:text-xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {getLangField(result.dominant_pattern, "title", reportLang)}
                    </h2>
                  </div>
                </div>
                <p className={`text-sm leading-relaxed mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {getLangField(result.dominant_pattern, "description", reportLang)}
                </p>

                {/* iter312 — Educational disclaimer band (MDR Rule 11 transparency). */}
                <div className={`flex items-start gap-2 p-3 rounded-xl mb-4 text-[11px] leading-snug ${isDark ? "bg-amber-950/30 border border-amber-800/40 text-amber-200" : "bg-amber-50 border border-amber-200 text-amber-800"}`} data-testid="assessment-educational-band">
                  <span aria-hidden>📚</span>
                  <span>
                    {reportLang === "bg"
                      ? "Това е образователен анализ на симптоми — не е медицинска диагноза и не замества консултация с лекар. Nutramilo не е медицинско изделие."
                      : reportLang === "es"
                      ? "Este es un analisis educativo de sintomas, no un diagnostico medico ni reemplazo de consulta con un medico."
                      : "This is an educational symptom analysis — not a medical diagnosis and not a substitute for clinical consultation. Nutramilo is not a medical device."}
                  </span>
                </div>

                {/* Severity */}
                <div className={`p-3 rounded-xl mb-4 ${isDark ? "bg-[hsl(223,47%,12%)]" : "bg-gray-50"}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className={`text-xs font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {/* iter312 — was: "Сериозност" (Severity). Reframed to "Match score" — purely
                          a pattern-match indicator, NOT a clinical severity rating per MDR Rule 11. */}
                      {reportLang === "bg" ? "Степен на съвпадение" : reportLang === "es" ? "Coincidencia" : "Match score"}
                    </span>
                    <span className="text-sm font-bold" style={{ color: result.dominant_pattern.color }} data-testid="severity-value">{result.severity}%</span>
                  </div>
                  <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-[hsl(223,47%,18%)]" : "bg-gray-200"}`}>
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: `${result.severity}%`, backgroundColor: result.dominant_pattern.color }} />
                  </div>
                </div>

                {/* Matched symptoms */}
                <div className="flex flex-wrap gap-1.5">
                  {result.matched_symptoms.map(s => {
                    const sym = symptoms.find(x => x.id === s);
                    return (
                      <span key={s} className={`text-[11px] px-2 py-1 rounded-full font-medium ${isDark ? "bg-[hsl(223,47%,14%)] text-gray-300" : "bg-gray-100 text-gray-600"}`}>
                        {sym ? getLangField(sym, "label", reportLang) : s}
                      </span>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Secondary pattern */}
            {result.secondary_pattern && (
              <div className={`flex items-center gap-3 p-3 rounded-xl mb-6 ${isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,18%)]" : "bg-white border border-gray-100"}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${result.secondary_pattern.color}15` }}>
                  <Activity className="w-4 h-4" style={{ color: result.secondary_pattern.color }} />
                </div>
                <div>
                  <p className={`text-[10px] uppercase tracking-wider ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                    {reportLang === "bg" ? "Вторичен модел" : reportLang === "es" ? "Patron secundario" : "Secondary pattern"}
                  </p>
                  <p className={`text-xs font-semibold ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    {getLangField(result.secondary_pattern, "title", reportLang)}
                  </p>
                </div>
              </div>
            )}

            <Button
              onClick={() => setStep(4)}
              className="gradient-primary text-white w-full h-12 rounded-xl text-sm font-semibold"
              data-testid="see-impact-btn"
            >
              {/* iter312 — was: "Виж последиците за вас" / "See what this means for you"
                  Both implied diagnostic consequence framing. MDR-safe rewrite. */}
              {reportLang === "bg" ? "Виж теми за разговор с лекар" : reportLang === "es" ? "Ver temas para hablar con su medico" : "See topics to discuss with your doctor"}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
            <div className="mt-3 flex justify-center">
              <SaveToPmosButton
                module="health_intelligence"
                bg={reportLang === "bg"}
                payload={{
                  source: "symptom_assessment",
                  dominant_pattern: result.dominant_pattern?.id,
                  severity: result.severity,
                  matched_symptoms: result.matched_symptoms?.map(s => s.id || s.label),
                  snapshot_date: new Date().toISOString().slice(0, 10),
                }}
                note="Symptom assessment result"
                testId="symptom-assessment-save-to-pmos"
              />
            </div>
          </div>
        )}

        {/* Step 4: Impact (4 consequences + aging accelerator callout) */}
        {step === 4 && result && (
          <div className="animate-fade-in-up" data-testid="step-impact">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 mb-3">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {/* iter312 — neutral, non-prognostic framing (was: "Какво означава това за вас?"). */}
                {reportLang === "bg" ? "Теми, свързани с този модел" : reportLang === "es" ? "Temas relacionados con este patron" : "Topics related to this pattern"}
              </h2>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {/* iter312 — was: "Ако не се адресира, този модел може да доведе до:" — implied
                    a prognostic/diagnostic consequence chain. MDR-safe rewrite. */}
                {reportLang === "bg" ? "Често обсъждани симптоми и теми за разговор с лекар:" : reportLang === "es" ? "Sintomas comunes y temas para hablar con su medico:" : "Common symptoms and topics to discuss with your doctor:"}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {(() => {
                // Dynamic impacts from backend, with safe fallback
                const impacts = (reportLang === "bg"
                  ? result.dominant_pattern?.impacts_bg
                  : reportLang === "es"
                  ? result.dominant_pattern?.impacts_es
                  : result.dominant_pattern?.impacts_en) || [];
                const iconCycle = [TrendingDown, Zap, Activity, Heart];
                const colorCycle = ["#F59E0B", "#EC4899", "#8B5CF6", "#EF4444"];
                return impacts.map((label, i) => {
                  const Icon = iconCycle[i % iconCycle.length];
                  const color = colorCycle[i % colorCycle.length];
                  return (
                    <div key={`${i}-${(label || '').slice(0, 24)}`} className={`flex items-start gap-3 p-4 rounded-xl ${isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,18%)]" : "bg-white border border-gray-100 shadow-sm"}`} data-testid={`impact-item-${i}`}>
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}15` }}>
                        <Icon className="w-5 h-5" style={{ color }} />
                      </div>
                      <p className={`text-sm font-medium pt-1.5 ${isDark ? "text-gray-200" : "text-[#1A1A2E]"}`}>{label}</p>
                    </div>
                  );
                });
              })()}
            </div>

            {/* Future risk — pattern-specific reversibility callout */}
            <div className={`rounded-xl p-4 mb-6 border-2 ${isDark ? "bg-amber-950/30 border-amber-800/50" : "bg-amber-50 border-amber-200"}`} data-testid="reversibility-callout">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-amber-500 to-orange-500">
                  <Activity className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className={`text-sm font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-800"}`}>
                    {/* iter312 — was: "Добрата новина: това е обратимо" — therapeutic claim.
                        Replaced with educational, non-promissory framing. */}
                    {reportLang === "bg" ? "Какво ще ви помогнем да направите" : reportLang === "es" ? "Como podemos ayudarle" : "How we can help"}
                  </h3>
                  <p className={`text-xs leading-relaxed ${isDark ? "text-amber-200/80" : "text-amber-700"}`}>
                    {(reportLang === "bg"
                      ? result.dominant_pattern?.reversibility_bg
                      : reportLang === "es"
                      ? result.dominant_pattern?.reversibility_es
                      : result.dominant_pattern?.reversibility_en) || ""}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setStep(3)} className="h-11" data-testid="impact-back-btn">
                <ChevronLeft className="w-4 h-4 mr-1" /> {reportLang === "bg" ? "Назад" : "Back"}
              </Button>
              <Button
                onClick={() => setStep(5)}
                className="gradient-primary text-white flex-1 h-12 rounded-xl text-sm font-semibold"
                data-testid="impact-next-btn"
              >
                {reportLang === "bg" ? "Виж моя персонализиран план" : reportLang === "es" ? "Ver mi plan personalizado" : "See my personalized plan"}
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 5: Email capture — only for anonymous visitors, auto-skip for auth'd / already-captured */}
        {step === 5 && result && !user && !emailCaptured && (
          <div className="animate-fade-in-up" data-testid="step-email-capture">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-sky-500 mb-4">
                <CheckCircle2 className="w-7 h-7 text-white" />
              </div>
              <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {reportLang === "bg" ? "Вашият метаболитен доклад е готов" : reportLang === "es" ? "Su informe metabolico esta listo" : "Your metabolic report is ready"}
              </h2>
              <p className={`text-sm max-w-md mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {reportLang === "bg"
                  ? "Къде да го изпратим? Ще можете да го отворите по всяко време — дори след като затворите таба."
                  : reportLang === "es"
                  ? "Donde se lo enviamos? Podra abrirlo en cualquier momento."
                  : "Where should we send it? You'll be able to open it any time — even after closing this tab."}
              </p>
            </div>

            <Card className={`mb-4 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-100 shadow-sm"}`}>
              <CardContent className="p-5">
                <label className={`text-xs font-medium mb-2 block ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                  {reportLang === "bg" ? "Вашият имейл" : reportLang === "es" ? "Su email" : "Your email"}
                </label>
                <input
                  type="email"
                  value={leadEmail}
                  onChange={(e) => setLeadEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={`w-full rounded-lg px-3 py-2.5 text-sm border focus:ring-1 focus:ring-[#0EA5E9] focus:border-[#0EA5E9] ${
                    isDark ? "bg-[hsl(223,47%,14%)] border-[hsl(223,47%,20%)] text-gray-200" : "bg-white border-gray-200"
                  }`}
                  data-testid="email-capture-input"
                  autoFocus
                />
                <p className={`text-[11px] mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {reportLang === "bg"
                    ? "Без спам. Само вашия доклад + поддръжка когато ви трябва."
                    : reportLang === "es"
                    ? "Sin spam. Solo su informe + soporte cuando lo necesite."
                    : "No spam. Only your report + support when you need it."}
                </p>
              </CardContent>
            </Card>

            <div className="space-y-3">
              <Button
                onClick={async () => {
                  const em = leadEmail.trim().toLowerCase();
                  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) {
                    toast.error(reportLang === "bg" ? "Моля въведете валиден имейл" : "Please enter a valid email");
                    return;
                  }
                  setEmailCaptureLoading(true);
                  try {
                    await axios.post(`${API}/leads/capture-email`, {
                      email: em, assessment_id: result.assessment_id, language: reportLang,
                    });
                    // Local resume backup — see useEffect above.
                    try {
                      localStorage.setItem("nutramilo_anon_resume", JSON.stringify({
                        assessment_id: result.assessment_id, email: em,
                      }));
                    } catch { /* localStorage full / disabled */ }
                    setEmailCaptured(true);
                    toast.success(reportLang === "bg"
                      ? "Готово! Изпратихме доклада на имейла ви."
                      : reportLang === "es"
                        ? "Listo! Hemos enviado el informe a su correo."
                        : "Done! We've sent the report to your inbox.");
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || (reportLang === "bg" ? "Грешка" : "Error"));
                  } finally { setEmailCaptureLoading(false); }
                }}
                disabled={emailCaptureLoading}
                className="gradient-primary text-white w-full h-12 rounded-xl text-sm font-semibold"
                data-testid="email-capture-submit"
              >
                {emailCaptureLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                  <>{reportLang === "bg" ? "Изпрати ми доклада" : reportLang === "es" ? "Enviame el informe" : "Send me the report"} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
              <button
                onClick={() => setEmailCaptured(true)}
                className={`w-full h-11 rounded-xl text-sm font-medium transition-colors ${isDark ? "text-gray-400 hover:text-white hover:bg-[hsl(223,47%,12%)]" : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
                data-testid="email-capture-skip"
              >
                {reportLang === "bg" ? "Продължи без имейл" : reportLang === "es" ? "Continuar sin email" : "Skip and continue"}
              </button>
            </div>
          </div>
        )}

        {/* Step 5 (paywall) — shown to auth'd users OR after email capture/skip */}
        {step === 5 && result && (user || emailCaptured) && (
          <div className="animate-fade-in-up" data-testid="step-paywall">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] mb-4">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h2 className={`text-xl sm:text-2xl font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {reportLang === "bg" ? "Получете вашия персонализиран метаболитен план" : reportLang === "es" ? "Obtenga su plan metabolico personalizado" : "Get your personalized metabolic plan"}
              </h2>
              <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {reportLang === "bg" ? "Подредени стъпки за 7 дни — базирани на вашия метаболитен модел." : reportLang === "es" ? "Pasos estructurados por 7 dias — basado en su patron." : "Structured 7-day steps — based on your pattern."}
              </p>
            </div>

            {/* Feature list */}
            <ul className={`space-y-2 mb-5 rounded-xl p-4 ${isDark ? "bg-[hsl(223,47%,10%)] border border-[hsl(223,47%,18%)]" : "bg-white border border-gray-100 shadow-sm"}`}>
              {[
                { bg: "Персонализирано хранене", en: "Personalized nutrition", es: "Nutricion personalizada" },
                { bg: "Време за прием на храна (циркаден ритъм)", en: "Meal timing (circadian rhythm)", es: "Horarios de comida (ritmo circadiano)" },
                { bg: "Инсулинова оптимизация", en: "Insulin optimization", es: "Optimizacion de insulina" },
                { bg: "Структуриран дневен план", en: "Structured daily plan", es: "Plan diario estructurado" },
              ].map((f, i) => (
                <li key={f.en} className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                  <span className={isDark ? "text-gray-200" : "text-gray-700"}>
                    {reportLang === "bg" ? f.bg : reportLang === "es" ? f.es : f.en}
                  </span>
                </li>
              ))}
            </ul>

            {/* iter396 — geo-aware 2-tier paywall (Core + Pro) */}
            {(() => {
              const corePlan = paywallPlans?.core;
              const proPlan = paywallPlans?.pro;
              const corePrice = annualBilling
                ? (corePlan?.monthly_equivalent_annual ?? 8.25)
                : (corePlan?.price ?? 9.90);
              const proPrice = annualBilling
                ? (proPlan?.monthly_equivalent_annual ?? 16.58)
                : (proPlan?.price ?? 19.90);
              const coreAnnual = corePlan?.price_annual ?? 99.0;
              const proAnnual = proPlan?.price_annual ?? 199.0;
              const standardCore = corePlan?.price_eu_standard ?? 12.90;
              const standardPro = proPlan?.price_eu_standard ?? 24.90;
              const showBgDiscount = (paywallPlans?.market === "bg") || (corePlan && corePlan.price < (corePlan.price_eu_standard ?? 999));

              const fmt = (v) => `€${Number(v).toFixed(2).replace(/\.00$/, "")}`;
              const triggerCheckout = async (planId) => {
                // iter690: Stripe премахнат — навигираме към Pricing (Wise modal)
                setPlanCheckoutLoading(planId);
                try {
                  const params = new URLSearchParams({
                    plan: planId,
                    period: annualBilling ? "annual" : "monthly",
                  });
                  navigate(`/pricing?${params.toString()}`);
                } catch (e) {
                  toast.error(e.response?.data?.detail || (bg ? "Грешка" : "Error"));
                } finally { setPlanCheckoutLoading(null); }
              };

              return (
                <>
                  {/* Annual / monthly toggle */}
                  <div className="flex items-center justify-center gap-2 mb-4" data-testid="paywall-billing-toggle">
                    <button
                      onClick={() => setAnnualBilling(false)}
                      data-testid="paywall-toggle-monthly"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${!annualBilling ? "bg-gradient-to-r from-[#5e227f] to-[#e44288] text-white" : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {reportLang === "bg" ? "Месечно" : "Monthly"}
                    </button>
                    <button
                      onClick={() => setAnnualBilling(true)}
                      data-testid="paywall-toggle-annual"
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${annualBilling ? "bg-gradient-to-r from-[#5e227f] to-[#e44288] text-white" : isDark ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
                    >
                      {reportLang === "bg" ? "Годишно" : "Annual"}
                      <span className="ml-1 text-[10px] font-bold text-emerald-500">−20%</span>
                    </button>
                  </div>

                  {/* 2-column tier grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3" data-testid="paywall-tier-grid">
                    {/* Core */}
                    <Card className={`${isDark ? "bg-gradient-to-br from-[hsl(223,47%,12%)] to-[hsl(223,47%,8%)] border-[#0EA5E9]/30" : "bg-gradient-to-br from-white to-[#F0F9FF] border-[#0EA5E9]/30"} border-2`} data-testid="paywall-tier-core">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-sky-400" : "text-sky-600"}`}>
                              {reportLang === "bg" ? "Стартирай" : reportLang === "es" ? "Empezar" : "Starter"}
                            </p>
                            <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>Core</h3>
                          </div>
                          <div className="text-right">
                            {showBgDiscount && (
                              <p className={`text-[10px] line-through ${isDark ? "text-gray-500" : "text-gray-400"}`}>{fmt(standardCore)}</p>
                            )}
                            <p className="text-xl font-bold" style={{ color: "#0EA5E9" }}>{fmt(corePrice)}</p>
                            <p className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                              {reportLang === "bg" ? (annualBilling ? "/мес (год.)" : "/ месец") : annualBilling ? "/mo (annual)" : "/ month"}
                            </p>
                          </div>
                        </div>
                        <ul className={`text-[11px] space-y-1 mb-3 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {[
                            { bg: "40 AI анализа/месец", en: "40 AI analyses/month", es: "40 analisis IA/mes" },
                            { bg: "Хранителен дневник + Plate Simulator", en: "Diary + Plate Simulator", es: "Diario + simulador" },
                            { bg: "DIL/NIS/UIS индекси", en: "DIL/NIS/UIS indices", es: "Indices DIL/NIS/UIS" },
                            { bg: "PDF доклади", en: "PDF reports", es: "Informes PDF" },
                          ].map((f) => (
                            <li key={f.en} className="flex items-center gap-1.5">
                              <CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" />
                              <span>{reportLang === "bg" ? f.bg : reportLang === "es" ? f.es : f.en}</span>
                            </li>
                          ))}
                        </ul>
                        {user ? (
                          <Button
                            onClick={() => triggerCheckout("core")}
                            disabled={planCheckoutLoading !== null}
                            className="bg-gradient-to-r from-sky-500 to-blue-500 hover:from-sky-600 hover:to-blue-600 text-white w-full h-10 rounded-lg text-xs font-semibold"
                            data-testid="paywall-core-btn"
                          >
                            {planCheckoutLoading === "core" ? <Loader2 className="w-4 h-4 animate-spin" /> :
                              (reportLang === "bg" ? "Започни Core" : reportLang === "es" ? "Iniciar Core" : "Start Core")}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => navigate("/register", { state: { assessmentId: result.assessment_id, intent: "core" } })}
                            className="bg-gradient-to-r from-sky-500 to-blue-500 text-white w-full h-10 rounded-lg text-xs font-semibold"
                            data-testid="paywall-core-register-btn"
                          >
                            {reportLang === "bg" ? "Регистрация + Core" : reportLang === "es" ? "Registro + Core" : "Register + Core"}
                          </Button>
                        )}
                      </CardContent>
                    </Card>

                    {/* Pro — highlighted */}
                    <Card className={`${isDark ? "bg-gradient-to-br from-[#5e227f]/20 to-[#e44288]/10 border-[#e44288]/40" : "bg-gradient-to-br from-[#FDF4FF] to-[#FCE7F3] border-[#e44288]/40"} border-2 relative`} data-testid="paywall-tier-pro">
                      <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: "linear-gradient(135deg, #5e227f, #e44288)" }}>
                        {reportLang === "bg" ? "★ Най-популярен" : reportLang === "es" ? "★ Mas popular" : "★ Most popular"}
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "#e44288" }}>
                              {reportLang === "bg" ? "Пълен профил" : reportLang === "es" ? "Perfil completo" : "Full profile"}
                            </p>
                            <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>Pro</h3>
                          </div>
                          <div className="text-right">
                            {showBgDiscount && (
                              <p className={`text-[10px] line-through ${isDark ? "text-gray-500" : "text-gray-400"}`}>{fmt(standardPro)}</p>
                            )}
                            <p className="text-xl font-bold" style={{ color: "#e44288" }}>{fmt(proPrice)}</p>
                            <p className={`text-[9px] ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                              {reportLang === "bg" ? (annualBilling ? "/мес (год.)" : "/ месец") : annualBilling ? "/mo (annual)" : "/ month"}
                            </p>
                          </div>
                        </div>
                        <ul className={`text-[11px] space-y-1 mb-3 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          {[
                            { bg: "120 AI анализа/месец", en: "120 AI analyses/month", es: "120 analisis IA/mes" },
                            { bg: "VAIS + Digital Twin Horizons", en: "VAIS + Digital Twin", es: "VAIS + gemelo digital" },
                            { bg: "Лаборатория OCR + биологична възраст", en: "Lab OCR + biological age", es: "Lab OCR + edad biologica" },
                            { bg: "Recipe Vision + AI клиничен анализ", en: "Recipe Vision + AI clinical", es: "Recipe Vision + clinico IA" },
                            { bg: "Всичко от Core", en: "Everything in Core", es: "Todo de Core" },
                          ].map((f) => (
                            <li key={f.en} className="flex items-center gap-1.5">
                              <CheckCircle2 className="w-3 h-3 flex-shrink-0" style={{ color: "#e44288" }} />
                              <span>{reportLang === "bg" ? f.bg : reportLang === "es" ? f.es : f.en}</span>
                            </li>
                          ))}
                        </ul>
                        {user ? (
                          <Button
                            onClick={() => triggerCheckout("pro")}
                            disabled={planCheckoutLoading !== null}
                            className="text-white w-full h-10 rounded-lg text-xs font-semibold shadow-md"
                            style={{ background: "linear-gradient(135deg, #5e227f, #e44288)" }}
                            data-testid="paywall-pro-btn"
                          >
                            {planCheckoutLoading === "pro" ? <Loader2 className="w-4 h-4 animate-spin" /> :
                              (reportLang === "bg" ? "Започни Pro" : reportLang === "es" ? "Iniciar Pro" : "Start Pro")}
                          </Button>
                        ) : (
                          <Button
                            onClick={() => navigate("/register", { state: { assessmentId: result.assessment_id, intent: "pro" } })}
                            className="text-white w-full h-10 rounded-lg text-xs font-semibold shadow-md"
                            style={{ background: "linear-gradient(135deg, #5e227f, #e44288)" }}
                            data-testid="paywall-pro-register-btn"
                          >
                            {reportLang === "bg" ? "Регистрация + Pro" : reportLang === "es" ? "Registro + Pro" : "Register + Pro"}
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {annualBilling && (
                    <p className={`text-[10px] text-center mb-2 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} data-testid="paywall-annual-hint">
                      {reportLang === "bg"
                        ? `Годишно плащане: Core ${fmt(coreAnnual)}/год · Pro ${fmt(proAnnual)}/год — спестявате 20%`
                        : `Annual: Core ${fmt(coreAnnual)}/yr · Pro ${fmt(proAnnual)}/yr — save 20%`}
                    </p>
                  )}
                  {showBgDiscount && !annualBilling && (
                    <p className={`text-[10px] text-center mb-2 ${isDark ? "text-emerald-400" : "text-emerald-600"}`} data-testid="paywall-bg-hint">
                      {reportLang === "bg"
                        ? "Регионално-адаптирани цени за BG пазар"
                        : "Regionally-adjusted price for your market"}
                    </p>
                  )}
                </>
              );
            })()}

            {/* Secondary: €1.99 micro-unlock */}
            <Card className={`mb-3 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"} border`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                      {reportLang === "bg" ? "Отключи за €1.99" : reportLang === "es" ? "Desbloquear por €1.99" : "Unlock for €1.99"}
                    </p>
                    <p className={`text-[11px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      {reportLang === "bg" ? "Еднократно · 7 дни пълен достъп" : reportLang === "es" ? "Unico · 7 dias acceso completo" : "One-time · 7-day full access"}
                    </p>
                  </div>
                  {user ? (
                    <Button
                      onClick={() => {
                        // iter690: Wise modal в Pricing
                        navigate("/pricing?intent=micro_unlock");
                      }}
                      variant="outline"
                      className="h-10 rounded-lg text-xs font-semibold"
                      data-testid="paywall-micro-btn"
                    >
                      {reportLang === "bg" ? "Отключи" : reportLang === "es" ? "Desbloquear" : "Unlock"}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => navigate("/register", { state: { assessmentId: result.assessment_id, intent: "micro_unlock" } })}
                      variant="outline"
                      className="h-10 rounded-lg text-xs font-semibold"
                      data-testid="paywall-micro-register-btn"
                    >
                      {reportLang === "bg" ? "Регистрирай и отключи" : reportLang === "es" ? "Registrar y desbloquear" : "Register & unlock"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Tertiary: Continue free (fallback funnel) */}
            <div className="text-center">
              <button
                onClick={async () => {
                  if (user) {
                    // Don't mark onboarded — the real Health Assessment at
                    // /onboarding is the only place that completes onboarding.
                    try {
                      await axios.post(`${API}/trial/start`, { assessment_id: result.assessment_id });
                      trackTrialStarted("core", 7);
                    } catch { /* already has trial — non-blocking */ }
                    try { localStorage.removeItem(LS_KEY); } catch {}
                    // Auth'd user without completed onboarding → /onboarding
                    if (!user?.profile?.completedOnboarding) {
                      navigate("/onboarding");
                    } else {
                      navigate("/dashboard");
                    }
                  } else {
                    navigate("/register", { state: { assessmentId: result.assessment_id, intent: "free" } });
                  }
                }}
                className={`text-sm font-medium underline-offset-2 hover:underline ${isDark ? "text-gray-400 hover:text-gray-200" : "text-gray-500 hover:text-gray-700"}`}
                data-testid="paywall-continue-free-btn"
              >
                {reportLang === "bg" ? "Продължи безплатно — ще добавиш данни постепенно" : reportLang === "es" ? "Continuar gratis — agregara datos gradualmente" : "Continue for free — add data progressively"}
              </button>
              {!user && (
                <p className={`text-[11px] mt-2 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                  {reportLang === "bg" ? "Безплатен 30-дневен пробен период. Без кредитна карта." : reportLang === "es" ? "Prueba gratuita de 30 dias. Sin tarjeta." : "Free 30-day trial. No credit card."}
                </p>
              )}
            </div>

            {!user && (
              <p className={`text-center mt-6 text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {reportLang === "bg" ? "Вече имате акаунт?" : reportLang === "es" ? "Ya tiene una cuenta?" : "Already have an account?"}{" "}
                <button onClick={() => navigate("/login")} className="text-[#0EA5E9] font-medium hover:underline">
                  {reportLang === "bg" ? "Вход" : reportLang === "es" ? "Iniciar sesion" : "Login"}
                </button>
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
